import { AreaConfiguration } from 'configuration/AreaConfiguration';
import { Router } from '@remix-run/router';
import _ from 'lodash';

export type ViewNavigatorOptions = {
  loginRoute: string;
  forbiddenRoute: string;
  notFoundRoute: string;
  clientInternalError: string;
  unauthorized: string;
  internalServerErrorRoute: string;
  areas: AreaConfiguration[];
  home: string;
};

const navigatorOptionsDefaults: ViewNavigatorOptions = {
  //client
  loginRoute: '/auth/login',
  forbiddenRoute: '/errors/forbidden', //(403)
 // forbiddenImpersonation: '/errors/security-alert', //(403.1)
  unauthorized: '/errors/unauthorized', //(401.1)
  notFoundRoute: '/errors/not-found', //(404.1)
  clientInternalError: '/errors/internal-client-error', //(500.1)
  //server
  internalServerErrorRoute: '/errors/internal-server-error', //(500)
//  notFoundResource: '/errors/not-found-resource', //(404)
 // unauthorizedNoAccount: '/errors/unauthorized-no-account', //(401.1)

  areas: [],
  home: '/',
};

export interface IViewNavigator {
  navigateToExternalUrl: (url: string) => Promise<void>;
  navigateToPath: (
    path: string | number,
    options: { relative: boolean }
  ) => Promise<void>;
  navigateToNotFound(): Promise<void>;
  navigateToUnauthorized(): Promise<void>;
  navigateToLogin(): Promise<void>;
  navigate(route: string | number, options: { relative: boolean }): void;
  getCurrentArea(): AreaConfiguration;
  getCurrentUrl(): string;
  getCurrentUrlExcept(): string | undefined;
}

export class ViewNavigator implements IViewNavigator {
  options: ViewNavigatorOptions = navigatorOptionsDefaults;
  private router: Router | undefined = undefined;
  constructor(options?: Partial<ViewNavigatorOptions>) {
    this.options =
      options === undefined
        ? this.options
        : Object.assign(this.options, options);
  }
  setRouter(router: Router) {
    this.router = router;
  }
  setAreas(areas: AreaConfiguration[]) {
    this.options.areas = areas;
  }
  navigateToExternalUrl(url: string): Promise<void> {
    window.open(url);
    return Promise.resolve();
  }

  navigate(
    route: string | number,
    options: { relative: boolean } = { relative: false }
  ) {
    if (_.isInteger(route)) {
      return this.router!.navigate(route as number);
    }
    if (route === window.location.pathname) {
      return Promise.reject();
    }
    if (options.relative) {
      const relativeRoute = `${this.getCurrentUrl()}/${route}`;
      return this.router!.navigate(relativeRoute);
    }
    return this.router!.navigate(route as string);
  }

  getCurrentArea(): AreaConfiguration {
    const firstPath = window.location.pathname
      .split('/')
      .filter((x) => x !== '')[0];
    let area = this.options.areas.filter((x) => x.name === firstPath);
    if (area === undefined || area.length === 0) {
      return this.options.areas.filter((x) => x.name === 'public')[0];
    } else {
      return area[0];
    }
  }

  getCurrentUrl(): string {
    const path = window.location.pathname;
    return path;
  }

  getCurrentUrlExcept(): string | undefined {
    const path = this.getCurrentUrl();
    const nonRedirectableRoutes = [this.options.loginRoute, this.options.home];
    const result =
      nonRedirectableRoutes.findIndex((x) => x === path) > -1
        ? undefined
        : path;
    return result;
  }

  navigateToPath(
    path: string | number,
    options: { relative: boolean } = { relative: false }
  ): Promise<void> {
    this.navigate(path, options);
    return Promise.resolve();
  }
  navigateToInternalServerError(): Promise<void> {
    this.navigate(this.options.internalServerErrorRoute);
    return Promise.resolve();
  }
  navigateToNotFound(): Promise<void> {
    this.navigate(this.options.notFoundRoute);
    return Promise.resolve();
  }
  navigateToUnauthorized(): Promise<void> {
    this.navigate(this.options.forbiddenRoute);
    return Promise.resolve();
  }
  navigateToLogin(): Promise<void> {
    this.navigate(this.options.loginRoute);
    return Promise.resolve();
  }
}
